import { useCallback } from 'react';
import { useLazyListMedicalMatchQuestionsQuery } from '@/api';
import { FailedLiveQuestion } from './storage';

/**
 * Reads which of the live questions that just left the processing queue failed,
 * and hands them to `onFailedQuestions`.
 *
 * `list-live-processing` only returns PROCESSING rows, so leaving that queue and
 * reaching a terminal status are the same event — a question missing from it has
 * already settled as ANSWERED or FAILED. The answers are pulled regardless of
 * what this finds, so all it has to report is the failures.
 *
 * Every failure inside the look-back the queue hands over is reported. That runs
 * back to the queue's last change, so a failure that landed while the panel was
 * closed or the tab hidden is still found. `useLiveQuestionNotices` owns which
 * of them are new and which the user has already cleared.
 *
 * Never rejects: a failed check costs a notice, and the answer refresh it runs
 * alongside must not be held up by it.
 */
export function useLiveQuestionFailures(
  caseId: string | undefined,
  onFailedQuestions: (questions: FailedLiveQuestion[]) => void,
): (lookBackSeconds: number) => Promise<void> {
  const [listQuestions] = useLazyListMedicalMatchQuestionsQuery();

  return useCallback(
    async (lookBackSeconds: number) => {
      if (!caseId) return;

      try {
        const { questions } = await listQuestions({
          caseId,
          askedWhen: ['LIVE'],
          status: ['FAILED'],
          maxTimeSinceUpdatedSeconds: lookBackSeconds,
        }).unwrap();

        onFailedQuestions(questions.map(({ questionId, question }) => ({ questionId, question })));
      } catch (error) {
        console.error('Failed to check for failed live Medical Match questions:', error);
      }
    },
    [caseId, listQuestions, onFailedQuestions],
  );
}
